<template>
  <v-menu
    v-if="hasAnyItemsInMenu"
    v-model="showingMenu"
    :close-on-content-click="false"
    location="bottom"
    eager
  >
    <template #activator="{ props }">
      <slot v-bind="{ props }" name="activator" />
    </template>

    <v-list data-cy="node-tools-bar-files-list">
      <v-list-item @click="selectAllFilesAndFolders" prepend-icon="mdi-checkbox-multiple-outline">
        <v-list-item-title>
          {{ $t('menus.tools.selectAll') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        @click="deselectAllFilesAndFolders"
        :disabled="!hasSelectedAnything"
        prepend-icon="mdi-checkbox-multiple-blank-outline"
      >
        <v-list-item-title>
          {{ $t('menus.tools.deselectAll') }}
        </v-list-item-title>
      </v-list-item>

      <v-divider aria-hidden="true" />

      <v-list-item
        v-if="!$isFCPExtension() && !$isAdobeExtension() && $canDownloadNodeAndPod()"
        @click="openDownloadAllDialog"
        :disabled="isCurrentNodeArchived || isAnythingArchived"
        data-cy="node-tools-bar-files-downloadAll"
        prepend-icon="mdi-folder-download"
      >
        <v-list-item-title>
          {{ $t('menus.tools.downloadAll') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="!$isFCPExtension() && !$isAdobeExtension() && $canDownloadNodeAndPod()"
        @click="openDownloadSelectedDialog"
        :disabled="!hasSelectedAnything || hasSelectedArchived"
        data-cy="node-tools-bar-files-downloadSelected"
        prepend-icon="mdi-download-box"
      >
        <v-list-item-title>
          {{ $t('menus.tools.downloadSelected') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="!$isFCPExtension() && !$isAdobeExtension() && $canDownloadNodeAndPod()"
        @click="downloadSelectedNotes"
        :disabled="!hasSelectedAnything || hasSelectedArchived"
        data-cy="node-tools-bar-files-downloadSelectedNotes"
        prepend-icon="mdi-note-text-outline"
      >
        <v-list-item-title>
          {{ $t('menus.tools.downloadSelectedNotes') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$isFCPExtension() && hasSelectedPodsForFCP && $canDownloadNodeAndPod()"
        @click="openFinalCutProDialog"
        :disabled="hasSelectedArchived"
        prepend-icon="mdi-download-box-outline"
      >
        <v-list-item-title>{{ $t('menus.tools.addAllToFCP') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$isAdobeExtension() && hasSelectedPods && $canDownloadNodeAndPod()"
        @click="openAdobePremiereDialog"
        :disabled="hasSelectedArchived"
        prepend-icon="mdi-download-box-outline"
      >
        <v-list-item-title>{{ $t('menus.tools.addAllToAP') }}</v-list-item-title>
      </v-list-item>

      <v-divider aria-hidden="true" />

      <v-list-item
        v-if="$canDeleteNodeAndPod()"
        @click="openDeleteAllDialog"
        data-cy="node-tools-bar-files-deleteAll"
        prepend-icon="mdi-trash-can"
        :disabled="!hasAnyItems"
      >
        <v-list-item-title>
          {{ $t('menus.tools.deleteAll') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canDeleteNodeAndPod()"
        @click="openDeleteSelectedDialog"
        :disabled="!hasSelectedAnything"
        data-cy="node-tools-bar-files-deleteSelected"
        prepend-icon="mdi-delete-sweep"
      >
        <v-list-item-title>
          {{ $t('menus.tools.deleteSelected') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canControlNodeAndPod()"
        @click="openCopySelectedDialog"
        :disabled="!hasSelectedAnything || hasSelectedArchived"
        data-cy="node-tools-bar-files-copySelected"
        prepend-icon="mdi-text-box-multiple-outline"
      >
        <v-list-item-title>
          {{ $t('menus.tools.copySelected') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canControlNodeAndPod()"
        @click="openMoveSelectedDialog"
        :disabled="!hasSelectedAnything || hasSelectedArchived"
        data-cy="node-tools-bar-files-moveSelected"
        prepend-icon="mdi-file-document-arrow-right"
      >
        <v-list-item-title>
          {{ $t('menus.tools.moveSelected') }}
        </v-list-item-title>
      </v-list-item>

      <v-divider v-if="$canRunPaidService()" aria-hidden="true" />

      <v-list-item
        v-if="$canRunPaidService()"
        :disabled="!hasSelectedPods || hasSelectedArchived"
        @click="openTranscribeSelectedDialog"
        prepend-icon="mdi-transcribe"
      >
        <v-list-item-title>
          {{ $t('menus.tools.generateSubtitles') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canRunPaidService() && $canLabelPods()"
        :disabled="!hasSelectedPods || hasSelectedArchived"
        @click="openLabelSelectedDialog"
        prepend-icon="mdi-label"
      >
        <v-list-item-title>
          {{ $t('menus.tools.labelDetect') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canControlNodeAndPod() && $canLabelPods()"
        :disabled="!hasSelectedAnything || hasSelectedArchived"
        @click="openSelectCustomLabelsDialog"
        prepend-icon="mdi-label"
      >
        <v-list-item-title>
          {{ $t('menus.tools.addLabels') }}
        </v-list-item-title>
      </v-list-item>

      <v-divider v-if="$isTeamUser() && $canControlNodeAndPod()" aria-hidden="true" />

      <v-list-item
        v-if="$canControlNodeAndPod() && $isTeamUser()"
        @click="openSetTeamonlyDialog"
        :disabled="!hasSelectedAnything || allItemsAreTeamonly || hasSelectedArchived"
        data-cy="node-tools-bar-files-setTeamonly"
        prepend-icon="mdi-folder-lock"
      >
        <v-list-item-title>
          {{ $t('routes.node.setTeamOnlyAccess') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canControlNodeAndPod() && $isTeamUser()"
        @click="openRemoveTeamonlyDialog"
        :disabled="!hasSelectedAnything || !hasSelectedTeamonlyItems || hasSelectedArchived"
        data-cy="node-tools-bar-files-setTeamonly"
        prepend-icon="mdi-folder-lock"
      >
        <v-list-item-title>
          {{ $t('routes.node.removeTeamOnlyAccess') }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>

  <div>
    <DownloadDialog
      v-if="!$isFCPExtension() && !$isAdobeExtension() && $canDownloadNodeAndPod()"
      :isDisabled="isCurrentNodeArchived || isAnythingArchived"
      :nodes="getFilteredNodes"
      :pods="getFilteredPods"
      v-model="showDownloadAllDialog"
    />

    <DownloadDialog
      v-if="!$isFCPExtension() && !$isAdobeExtension() && $canDownloadNodeAndPod()"
      :isDisabled="!hasSelectedAnything || hasSelectedArchived"
      :nodes="selectedNodes"
      :pods="selectedPods"
      v-model="showDownloadSelectedDialog"
    />

    <FinalCutProDialog
      v-if="$isFCPExtension() && hasSelectedPodsForFCP && $canDownloadNodeAndPod()"
      :disabled="hasSelectedArchived"
      :pods="selectedPodsForFCP"
      v-model="showFinalCutProDialog"
    />

    <AdobePremiereDialog
      v-if="$isAdobeExtension() && hasSelectedPods && $canDownloadNodeAndPod()"
      :disabled="hasSelectedArchived"
      :pods="selectedPods"
      v-model="showAdobePremiereDialog"
    />

    <DeleteSelectedDialog
      v-if="$canDeleteNodeAndPod()"
      v-model="showDeleteAllDialog"
      deletionType="all"
      :isDisabled="!hasAnyItems"
      :pods="getFilteredPods"
      :nodes="getFilteredNodes"
    />

    <DeleteSelectedDialog
      v-if="$canDeleteNodeAndPod()"
      v-model="showDeleteSelectedDialog"
      deletionType="selected"
      :isDisabled="!hasSelectedAnything"
      :pods="selectedPods"
      :nodes="selectedNodes"
    />

    <CopySelectedDialog
      v-if="$canControlNodeAndPod()"
      :isDisabled="!hasSelectedAnything || hasSelectedArchived"
      :nodes="selectedNodes"
      :pods="selectedPods"
      v-model="showCopySelectedDialog"
    />

    <MoveSelectedDialog
      v-if="$canControlNodeAndPod()"
      :isDisabled="!hasSelectedAnything || hasSelectedArchived"
      :nodes="selectedNodes"
      :pods="selectedPods"
      v-model="showMoveSelectedDialog"
    />

    <TranscribeSelectedDialog
      v-if="$canRunPaidService()"
      :pods="selectedPods"
      :disabled="!hasSelectedPods || hasSelectedArchived"
      transcriptionContext="contextMenu"
      v-model="showTranscribeSelectedDialog"
    />

    <LabelSelectedDialog
      v-if="$canRunPaidService()"
      :pods="selectedPods"
      :disabled="!hasSelectedPods || hasSelectedArchived"
      v-model="showLabelSelectedDialog"
    />

    <SelectLabelsDialog
      v-if="$canControlNodeAndPod()"
      v-model="showSelectCustomLabelsDialog"
      :disabled="!hasSelectedAnything || hasSelectedArchived"
      :is-processing-labels="isProcessingCustomLabels"
      :is-visible="isAddCustomLabelsDialogVisible"
      :show-custom-labels="true"
      @addLabels="addCustomLabels"
    />

    <UpdateTeamonlyDialog
      v-if="$canControlNodeAndPod() && $isTeamUser()"
      :isDisabled="!hasSelectedAnything || allItemsAreTeamonly || hasSelectedArchived"
      :pods="selectedPods"
      :nodes="selectedNodes"
      actionType="set"
      v-model="showSetTeamonlyDialog"
    />

    <UpdateTeamonlyDialog
      v-if="$canControlNodeAndPod() && $isTeamUser()"
      :isDisabled="!hasSelectedAnything || !hasSelectedTeamonlyItems|| hasSelectedArchived"
      :pods="selectedPods"
      :nodes="selectedNodes"
      actionType="remove"
      v-model="showRemoveTeamonlyDialog"
    />
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useAuthStore } from '@/stores/AuthStore.js'
import { useNodeStore } from '@/stores/NodeStore.js';
import { usePodLabelStore } from '@/stores/PodLabelStore.js';
import DownloadDialog from '../dialogs/DownloadDialog.vue'
import DeleteSelectedDialog from '../dialogs/DeleteSelectedDialog.vue'
import CopySelectedDialog from '../dialogs/CopySelectedDialog.vue'
import MoveSelectedDialog from '../dialogs/MoveSelectedDialog.vue'
import UpdateTeamonlyDialog from '../dialogs/UpdateTeamonlyDialog.vue'
import MimetypeHelper from '@/components/MimetypeHelper.js'
import FinalCutProDialog from '../dialogs/FinalCutProDialog.vue'
import AdobePremiereDialog from '../dialogs/AdobePremiereDialog.vue'
import TranscribeSelectedDialog from '../dialogs/TranscribeSelectedDialog.vue'
import LabelSelectedDialog from '../../../pods2/components/dialogs/LabelSelectedDialog.vue'
import SelectLabelsDialog from '@/routes/pods2/components/dialogs/SelectLabelsDialog.vue';
import { useFilterStore } from '@/stores/FilterStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';

export default {
  emits: ['update:modelValue'],
  name: "FilesContextMenu",
  components: {
    SelectLabelsDialog,
    DownloadDialog,
    DeleteSelectedDialog,
    CopySelectedDialog,
    MoveSelectedDialog,
    FinalCutProDialog,
    AdobePremiereDialog,
    TranscribeSelectedDialog,
    UpdateTeamonlyDialog,
    LabelSelectedDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isProcessingCustomLabels: false,
      isAddCustomLabelsDialogVisible: true,
      showDownloadAllDialog: false,
      showDownloadSelectedDialog: false,
      showFinalCutProDialog: false,
      showAdobePremiereDialog: false,
      showDeleteAllDialog: false,
      showDeleteSelectedDialog: false,
      showCopySelectedDialog: false,
      showMoveSelectedDialog: false,
      showTranscribeSelectedDialog: false,
      showLabelSelectedDialog: false,
      showSelectCustomLabelsDialog: false,
      showSetTeamonlyDialog: false,
      showRemoveTeamonlyDialog: false,
    }
  },
  methods: {
    openDownloadAllDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showDownloadAllDialog = true;
      });
    },
    openDownloadSelectedDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showDownloadSelectedDialog = true;
      });
    },
    downloadSelectedNotes() {
      const commonStore = useCommonStore();
      const payload = {
        nodeids: this.selectedNodes.map((node) => node.id),
        podids: this.selectedPods.map((pod) => pod.id),
      };

      this.showingMenu = false;
      commonStore.downloadMediaNotes(payload);
    },
    openFinalCutProDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showFinalCutProDialog = true;
      });
    },
    openAdobePremiereDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showAdobePremiereDialog = true;
      });
    },
    openDeleteAllDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showDeleteAllDialog = true;
      });
    },
    openDeleteSelectedDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showDeleteSelectedDialog = true;
      });
    },
    openCopySelectedDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showCopySelectedDialog = true;
      });
    },
    openMoveSelectedDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showMoveSelectedDialog = true;
      });
    },
    openTranscribeSelectedDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showTranscribeSelectedDialog = true;
      });
    },
    openLabelSelectedDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showLabelSelectedDialog = true;
      });
    },
    openSelectCustomLabelsDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showSelectCustomLabelsDialog = true;
      });
    },
    openSetTeamonlyDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showSetTeamonlyDialog = true;
      });
    },
    openRemoveTeamonlyDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showRemoveTeamonlyDialog = true;
      });
    },
    addCustomLabels(payload) {
      if (!payload?.labels || payload.labels.length === 0) {
        return;
      }

      this.isProcessingCustomLabels = true;
      this.isAddCustomLabelsDialogVisible = true;

      const self = this;

      const podLabelStore = usePodLabelStore();
      podLabelStore.createLabelsForPodsAndNodes({
        podIds: this.selectedPods.map((pod) => pod.id),
        nodeIds: this.selectedNodes.map((node) => node.id),
        labels: payload?.labels || [],
        includeSubfolders: payload?.includeSubfolders || false,
      }).then(() => {
        const labelsCount = payload?.labels ? payload.labels.length : 0;
        this.$showMessage(self.$t('routes.pod.labelsAdded', labelsCount), { type: 'success' });
      }).catch((error) => {
        const apiError = error?.response?.data?.error?.message;

        if (apiError) {
          this.$showMessage(apiError, { type: 'error' });
        } else {
          console.log(error);
        }
      }).finally(() => {
        self.isAddCustomLabelsDialogVisible = false;
        self.isProcessingCustomLabels = false;
      });
    },
    selectAllFilesAndFolders() {
      const podStore = usePodStore();
      const nodeStore = useNodeStore();
      const filterStore = useFilterStore();

      if (filterStore.hasFiltersApplied) {
        nodeStore.deselectAllNodes();
        podStore.deselectAllPods();

        for (const pod of filterStore.getFilteredList) {
          podStore.toggleSelectedPod(pod)
        }
      } else {
        const currentNodeId = this.currentNode.id;
        podStore.selectFilesInFolder({ nodeId: currentNodeId });
        nodeStore._selectSubfolders({ nodeId: currentNodeId });
      }

      this.showingMenu = false;
    },
    deselectAllFilesAndFolders() {
      const nodeStore = useNodeStore();
      nodeStore.deselectAllNodes();

      const podStore = usePodStore();
      podStore.deselectAllPods();

      this.showingMenu = false;
    },
  },
  computed: {
    showingMenu: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    hasAnyItemsInMenu() {
      // return this.$canControlNodeAndPod() ||
      //         this.$canDownloadNodeAndPod();
      return true;  // TODO: Probably check this out at some point
    },

    currentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    isCurrentNodeArchived() {
      return Boolean(this.currentNode && this.currentNode.archived);
    },

    // To download all pods use getAllPods or getFilteredList ?
    getAllNodes() {
      const nodeStore = useNodeStore();
      return nodeStore.getChildNodes;
    },
    getAllPods() {
      const podStore = usePodStore();
      return podStore.getPods;
    },
    getFilteredNodes() {
      const filterStore = useFilterStore();
      if (filterStore.hasFiltersApplied) {
        return []
      }

      return this.getAllNodes;
    },
    getFilteredPods() {
      const filterStore = useFilterStore();
      if (filterStore.hasFiltersApplied) {
        return filterStore.getFilteredList
      }

      return this.getAllPods;
    },
    selectedNodes() {
      const nodeStore = useNodeStore();
      return nodeStore.getSelectedNodes;
    },
    selectedPods() {
      const podStore = usePodStore();
      return podStore.getSelectedPods;
    },
    isAnythingArchived() {
      return this.getAllNodes.some(node => node.archived) || this.getAllPods.some(pod => pod.archived);
    },
    hasSelectedNodes() {
      return this.selectedNodes && this.selectedNodes.length > 0;
    },
    hasSelectedPods() {
      return this.selectedPods && this.selectedPods.length > 0;
    },
    hasSelectedAnything() {
      return this.hasSelectedNodes || this.hasSelectedPods;
    },
    hasSelectedArchived() {
      return this.selectedNodes.some(x => x.archived) || this.selectedPods.some(x => x.archived);
    },
    hasSelectedPodsForFCP() {
      return this.selectedPodsForFCP.length > 0;
    },
    selectedPodsForFCP() {
      const pods = this.selectedPods;
      const fcpPods = pods.filter((p) => MimetypeHelper.isSupportedByFCP(p));
      return fcpPods;
    },
    accessPointId() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    hasSelectedTeamonlyItems() {
      const selectedNodes = this.selectedNodes;
      const selectedPods = this.selectedPods;

      if (selectedNodes.length > 0) {
        const hasSelected = selectedNodes.some((node) => node.teamonly);
        if (hasSelected) {
          return true;
        }
      }

      if (selectedPods.length > 0) {
        return selectedPods.some((pod) => pod.teamonly);
      }

      return false;
    },
    allItemsAreTeamonly() {
      let allNodesAreTeamonly = null;
      let allPodsAreTeamonly = null;

      const selectedNodes = this.selectedNodes;
      const selectedPods = this.selectedPods;

      if (selectedNodes.length > 0) {
        allNodesAreTeamonly = selectedNodes.every((node) => node.teamonly);
      }

      if (selectedPods.length > 0) {
        allPodsAreTeamonly = selectedPods.every((pod) => pod.teamonly);
      }

      if (allNodesAreTeamonly && allPodsAreTeamonly) {
        return true;
      }

      if (allNodesAreTeamonly === null) {
        return allPodsAreTeamonly;
      }

      if (allPodsAreTeamonly === null) {
        return allNodesAreTeamonly;
      }

      return false;
    },
    hasAnyItems: function() {
      if (!this.getAllNodes || !this.getAllPods) {
        return false;
      }

      return this.getAllNodes.length > 0 || this.getAllPods.length > 0;
    },
  }
}
</script>

<style>
</style>
